import {useEffect, useMemo, useRef, useState} from "react";
import {collectionGroup, doc, onSnapshot, query, where} from "firebase/firestore";

import {useAuthState} from "@/auth/AuthProvider";
import {firestore} from "@/firebase/client";
import {isPlaybackActivityRecent} from "@recital-master/shared";

type SessionRecord = {
  familyAccountId: string;
  expiresAt: string | null;
  lastRefreshedAt: string | null;
  lastPlaybackActivityAt: string | null;
  revokedAt: string | null;
};

export type AdminWatchingPresence = {
  lastSessionActivityByFamily: ReadonlyMap<string, string>;
  watchingNow: number;
};

/**
 * Presence is the one Watching metric that should bypass the historical
 * report entirely. Session leases are small and bounded, and a local clock
 * makes an abandoned lease disappear exactly when it expires even if no
 * subsequent Firestore write arrives.
 */
export function useAdminWatchingPresence(enabled: boolean): AdminWatchingPresence | null {
  const {devMode, demoMode, isAdmin, user} = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const [sessions, setSessions] = useState<SessionRecord[] | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled || preview || !isAdmin || !firestore) {
      setSessions(null);
      return;
    }
    return onSnapshot(
      query(
        collectionGroup(firestore, "sessions"),
        where("expiresAt", ">", new Date().toISOString())
      ),
      (snapshot) => {
        setNowMs(Date.now());
        setSessions(snapshot.docs.flatMap((session) => {
          const familyAccountId = session.ref.parent.parent?.id;
          if (!familyAccountId || familyAccountId === user?.uid) return [];
          const value = session.data();
          return [{
            familyAccountId,
            expiresAt: typeof value.expiresAt === "string" ? value.expiresAt : null,
            lastRefreshedAt: typeof value.lastRefreshedAt === "string" ? value.lastRefreshedAt : null,
            lastPlaybackActivityAt: typeof value.lastPlaybackActivityAt === "string"
              ? value.lastPlaybackActivityAt
              : null,
            revokedAt: typeof value.revokedAt === "string" ? value.revokedAt : null
          }];
        }));
      },
      (error) => {
        console.warn("[admin-dashboard] live presence unavailable", error);
        setSessions(null);
      }
    );
  }, [enabled, isAdmin, preview, user?.uid]);

  useEffect(() => {
    if (!enabled || sessions == null) return;
    const timer = setInterval(() => setNowMs(Date.now()), 5_000);
    return () => clearInterval(timer);
  }, [enabled, sessions]);

  return useMemo(() => {
    if (sessions == null) return null;
    const liveFamilies = new Set<string>();
    const lastSessionActivityByFamily = new Map<string, string>();
    for (const session of sessions) {
      if (session.lastRefreshedAt) {
        const previous = lastSessionActivityByFamily.get(session.familyAccountId);
        if (!previous || session.lastRefreshedAt > previous) {
          lastSessionActivityByFamily.set(session.familyAccountId, session.lastRefreshedAt);
        }
      }
      const expiresAtMs = session.expiresAt ? Date.parse(session.expiresAt) : Number.NaN;
      if (
        session.revokedAt == null &&
        Number.isFinite(expiresAtMs) &&
        expiresAtMs > nowMs &&
        isPlaybackActivityRecent(session.lastPlaybackActivityAt, nowMs)
      ) {
        liveFamilies.add(session.familyAccountId);
      }
    }
    return {lastSessionActivityByFamily, watchingNow: liveFamilies.size};
  }, [nowMs, sessions]);
}

/**
 * One compact server-published revision replaces listeners over the raw
 * historical collections. The first snapshot establishes a baseline; later
 * revisions ask the existing authoritative callable for a fresh aggregate.
 */
export function useAdminDashboardRevision({
  enabled,
  onRevision
}: {
  enabled: boolean;
  onRevision: () => void;
}) {
  const {devMode, demoMode, isAdmin} = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const baseline = useRef<string | null | undefined>(undefined);
  const onRevisionRef = useRef(onRevision);
  onRevisionRef.current = onRevision;

  useEffect(() => {
    if (!enabled || preview || !isAdmin || !firestore) {
      baseline.current = undefined;
      return;
    }
    return onSnapshot(
      doc(firestore, "adminDashboardState", "current"),
      (snapshot) => {
        const value = snapshot.exists()
          ? `${String(snapshot.get("updatedAt") ?? "")}:${String(snapshot.get("eventId") ?? "")}`
          : null;
        if (baseline.current === undefined) {
          baseline.current = value;
          return;
        }
        if (baseline.current === value) return;
        baseline.current = value;
        onRevisionRef.current();
      },
      (error) => console.warn("[admin-dashboard] live revision unavailable", error)
    );
  }, [enabled, isAdmin, preview]);

  useEffect(() => {
    if (!enabled || preview || !isAdmin) return;
    const reportingHour = () => new Intl.DateTimeFormat("en-CA", {
      day: "2-digit",
      hour: "2-digit",
      hour12: false,
      month: "2-digit",
      timeZone: "America/Winnipeg",
      year: "numeric"
    }).format(new Date());
    let currentHour = reportingHour();
    // A rolling seven-day window and Central reporting day can advance with
    // no database write. This clock check performs at most one reconciliation
    // per reporting hour and only while the Watching view is actually open.
    const timer = setInterval(() => {
      const nextHour = reportingHour();
      if (nextHour === currentHour) return;
      currentHour = nextHour;
      onRevisionRef.current();
    }, 60_000);
    return () => clearInterval(timer);
  }, [enabled, isAdmin, preview]);
}
